/**
 * ╔════════════════════════════════════════════════════════════╗
 * ║  Dictionary<T>                                             ║
 * ║  ─────────────────────────────────────────────────────     ║
 * ║  Лучшее из мира Map и Array                                ║
 * ╠════════════════════════════════════════════════════════════╣
 * ║                                                            ║
 * ║  Map-like хранение данных, Array-like итератор             ║
 * ║                                                            ║
 * ║  • dict.get(id)     → O(1)                                 ║
 * ║  • Основные методы массива                                 ║
 * ║  • Итерируется как массив                                  ║
 * ║                                                            ║
 * ║  Note bene!                                                ║
 * ║  Методы массива возвращают Array<T>, не Dictionary<T>      ║
 * ║  Причина: Совместимость с контролами (VSelect и тд)        ║
 * ║                                                            ║
 * ╚════════════════════════════════════════════════════════════╝
 */

import Core from "@/core/Core";

/** ==================== Класс для хранения справочников ==================== **/
class Dictionary<K = number | string, V = any> extends Map<K, V> {
    /** Функция формирования ключа из элемента. По умолчанию — item.id */
    private _keyExtractor: (item: V) => K = (item: V) => (item as any).id;
    /** Задаёт функцию формирования ключа. Возвращает this для цепочки вызовов */
    useKeyExtractor(keyExtractor: (item: V) => K): this {
        this._keyExtractor = keyExtractor;
        return this;
    }

    /** Создание справочника из массива */
    static fromArray<V extends { id: number | string }>(array: V[]): Dictionary<number | string, V> {
        const dict = new Dictionary<number | string, V>();
        array?.forEach(item => {
            dict.set(item.id, item);
        });
        return dict;
    }

    /**
     * Добавить элемент или массив элементов.
     * Ключ формируется через _keyExtractor
     */
    add(value: V | V[]): void {
        if (Array.isArray(value)) {
            value.forEach(item => this.set(this._keyExtractor(item), item));
        } else {
            this.set(this._keyExtractor(value), value);
        }
    }

    /**
     * Заменить всё содержимое: очистка + добавление новых элементов.
     * Ссылку на справочник не меняет — мутирует на месте, поэтому reactive переживает обновление.
     */
    reset(value: V | V[]): void {
        this.clear();
        this.add(value);
    }

    /**
     * Преобразовать в массив значений
     */
    toArray(): V[] { return Array.from(this.values()); }

    /**
     * Итератор по значениям (как в массиве)
     * Позволяет использовать for...of напрямую по значениям
     */
    [Symbol.iterator](): IterableIterator<V> { return this.values(); }

    /**
     * forEach как в массиве (по значениям)
     */
    forEach(callback: (value: V, index: number, dictionary: this) => void): void {
        let index = 0;
        for (const value of this.values()) {
            callback(value, index++, this);
        }
    }

    /**
     * map как в массиве
     */
    map<U>(callback: (value: V, index: number) => U): U[] {
        const result: U[] = [];
        let index = 0;
        for (const value of this.values()) {
            result.push(callback(value, index++));
        }
        return result;
    }

    /**
     * filter как в массиве
     */
    filter(callback: (value: V, index: number) => boolean): V[] {
        const result: T[] = [];
        let index = 0;
        for (const value of this.values()) {
            if (callback(value, index++)) {
                result.push(value);
            }
        }
        return result;
    }
    /**
     * find как в массиве
     */
    find(callback: (value: V, index: number) => boolean): V | undefined {
        let index = 0;
        for (const value of this.values()) {
            if (callback(value, index++)) {
                return value;
            }
        }
        return undefined;
    }

    /**
     * reduce как в массиве
     */
    reduce<U>(callback: (accumulator: U, value: V, index: number) => U, initialValue: U): U {
        let result = initialValue;
        let index = 0;
        for (const value of this.values()) {
            result = callback(result, value, index++);
        }
        return result;
    }

    /**
     * Глубокое клонирование с сохранением keyExtractor.
     * Стандартный cloneDeep теряет _keyExtractor потому что Dictionary наследуется от Map,
     * а Map при клонировании не копирует кастомные свойства.
     */
    cloneDeep(): Dictionary<K, V> {
        const clone = new Dictionary<K, V>();
        clone._keyExtractor = this._keyExtractor;

        for (const [key, value] of this.entries()) {
            clone.set(key, Core.utils.cloneDeep(value));
        }

        return clone;
    }

    /**
     * Сериализация в массив (для JSON.stringify и отправки на сервер)
     */
    toJSON(): V[] { return this.toArray(); }
}

export default Dictionary;